import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Box,
  Typography,
  SelectChangeEvent,
  OutlinedInput,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "contexts/AuthContext";
import { ProviderDto } from "libs/cellera-sdk";
import { useCallback, useMemo, useState } from "react";
import { useCelleraTheme } from "theme/useCelleraTheme";

import { practiceProfileApi } from "../../../apis";

interface ProviderSearchDropDownProps {
  onChange: (value: ProviderDto | null) => void;
  label?: string;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function ProviderSearchDropDown({
  onChange,
  label = "Select Provider",
  disabled = false,
  error = false,
  helperText,
}: ProviderSearchDropDownProps) {
  const [value, setValue] = useState<ProviderDto | null>(null);
  const { colors } = useCelleraTheme();
  const { userClaims } = useAuth();
  const { isPracticeOwner = false } = userClaims || {};
  if (!isPracticeOwner) {
    return null;
  }

  const {
    data: providers = [],
    isLoading: loading,
    error: apiError,
  } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => {
      const result = await practiceProfileApi.getProvidersOfPractice();
      return result || [];
    },
    staleTime: 5 * 60 * 1000,
    enabled: isPracticeOwner,
  });

  // Handle selection change
  const handleChange = useCallback(
    (event: SelectChangeEvent<string>) => {
      const selectedValue = event.target.value as string;
      if (selectedValue === value?.id || selectedValue === "") {
        onChange(null);
        setValue(null);
        return;
      }
      const provider = providers.find(
        (provider) => provider.id === selectedValue
      );
      setValue(provider || null);
      onChange(provider || null);
    },
    [providers, value, onChange]
  );

  const renderProviderName = useCallback((provider: ProviderDto) => {
    return `${provider.firstName} ${provider.lastName} - ${provider.email}`;
  }, []);

  const contentDetails = useMemo(() => {
    if (loading) {
      return (
        <MenuItem>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CircularProgress size={20} />
          </Box>
        </MenuItem>
      );
    }
    if (providers.length === 0) {
      return (
        <MenuItem disabled>
          <Typography>No providers found</Typography>
        </MenuItem>
      );
    }
    return providers.map((provider) => (
      <MenuItem key={provider.id} value={provider.id}>
        <Box>
          <Typography variant="body2" fontWeight={500}>
            {renderProviderName(provider)}
          </Typography>
        </Box>
      </MenuItem>
    ));
  }, [loading, providers, renderProviderName]);

  return (
    <FormControl error={error || !!apiError}>
      <InputLabel id="provider-select-label">{label}</InputLabel>
      <Select
        labelId="provider-select-label"
        value={value?.id || ""}
        onChange={handleChange}
        disabled={disabled}
        input={<OutlinedInput label={label} />}
        renderValue={(selected) => {
          if (!selected) return "";
          const provider = providers.find((p) => p.id === selected);
          return provider ? renderProviderName(provider) : "";
        }}
        MenuProps={{
          PaperProps: {
            style: {
              maxHeight: 300,
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "2px",
            "& fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.light,
            },
            "&:hover fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.border.main,
            },
            "&.Mui-focused fieldset": {
              borderColor:
                error || apiError ? colors.error[500] : colors.secondary.main,
            },
          },
          width: "100%",
        }}
      >
        {contentDetails}
      </Select>
      {helperText && (
        <Typography
          variant="caption"
          color={error || apiError ? "error" : "text.secondary"}
          sx={{ mt: 0.5, ml: 1.5 }}
        >
          {helperText || (apiError ? "Failed to load providers" : undefined)}
        </Typography>
      )}
    </FormControl>
  );
}
